/* Add in place, then refresh and open the existing Flex cart drawer. */
(() => {
  'use strict';
  if (window.EffyProductMain) return;
  const initialized = new WeakSet();

  function focusDrawer(drawer, returnFocus, cart) {
    const originalAttributes = new Map(['role', 'aria-label', 'aria-modal', 'tabindex'].map(name => [name, drawer.getAttribute(name)]));
    drawer.setAttribute('role', 'dialog');
    drawer.setAttribute('aria-label', 'Shopping cart');
    drawer.setAttribute('aria-modal', 'true');
    drawer.setAttribute('tabindex', '-1');
    const focusable = () => [...drawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex="0"]')]
      .filter(element => element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
    const onKey = event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        cart.hideDrawer();
      } else if (event.key === 'Tab') {
        const elements = focusable();
        const first = elements[0];
        const last = elements[elements.length - 1];
        if (!first) { event.preventDefault(); drawer.focus(); }
        else if (event.shiftKey && (document.activeElement === first || document.activeElement === drawer)) {
          event.preventDefault(); last.focus();
        } else if (!event.shiftKey && (document.activeElement === last || document.activeElement === drawer)) {
          event.preventDefault(); first.focus();
        }
      }
    };
    const observer = new MutationObserver(() => {
      if (drawer.classList.contains('is-visible')) return;
      observer.disconnect();
      drawer.removeEventListener('keydown', onKey);
      originalAttributes.forEach((value, name) => {
        if (value === null) drawer.removeAttribute(name);
        else drawer.setAttribute(name, value);
      });
      if (returnFocus.isConnected) returnFocus.focus({ preventScroll: true });
    });
    observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    drawer.addEventListener('keydown', onKey);
    drawer.focus({ preventScroll: true });
  }

  async function refreshAndOpenCart(returnFocus) {
    const theme = window.PXUTheme;
    const cart = theme && theme.jsAjaxCart;
    const $ = window.jQuery;
    if (!cart || cart.cart_action !== 'drawer' || !theme.asyncView || !$) return false;
    // A distinct URL avoids reusing an initial cart request started before the add.
    const url = new URL(theme.routes.cart_url, window.location.origin);
    url.searchParams.set('effy_cart_refresh', String(Date.now()));
    const result = await new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error('Cart refresh timed out')), 20000);
      theme.asyncView.load(url.pathname + url.search, 'ajax')
        .done(value => { clearTimeout(timer); resolve(value); })
        .fail(() => { clearTimeout(timer); reject(new Error('Cart refresh failed')); });
    });
    if (!result.html || typeof result.html.content !== 'string' || !result.options) return false;
    $('[data-ajax-cart-content]').html(result.html.content);
    $('[data-bind="itemCount"]').text(result.options.item_count);
    $('[data-ajax-cart-trigger]').toggleClass('has-cart-count', result.options.item_count > 0);
    if (theme.currencyConverter) theme.currencyConverter.convertCurrencies();
    cart.showDrawer();
    const drawers = [...document.querySelectorAll('[data-ajax-cart-drawer].is-visible')];
    const drawer = drawers.reverse().find(element => element.getClientRects().length);
    if (drawer) focusDrawer(drawer, returnFocus, cart);
    return Boolean(drawer);
  }

  function initialize(root) {
    if (initialized.has(root)) return;
    initialized.add(root);
    const select = root.querySelector('[data-effy-variant]');
    const price = root.querySelector('[data-effy-price]');
    const compare = root.querySelector('[data-effy-compare]');
    const quantity = root.querySelector('[data-effy-quantity]');
    const add = root.querySelector('[data-effy-add]');
    const accelerated = root.querySelector('[data-effy-accelerated]');
    const availability = root.querySelector('[data-effy-availability]');
    const image = root.querySelector('[data-effy-main-image]');
    const thumbnails = [...root.querySelectorAll('[data-effy-thumbnail]')];
    if (!select || !quantity || !add) return;

    function showImage(data) {
      if (!image || !data.image) return;
      image.src = data.image;
      image.alt = data.imageAlt || '';
      thumbnails.forEach(button => {
        button.setAttribute('aria-pressed', String(button.dataset.imageId === data.imageId));
      });
    }

    function update(changeImage, changeUrl) {
      const option = select.selectedOptions[0];
      if (!option) {
        add.disabled = true;
        if (accelerated) { accelerated.hidden = true; accelerated.inert = true; }
        return;
      }
      const data = option.dataset;
      const available = data.available === 'true';
      const planRequired = root.dataset.planRequired === 'true';
      if (price) price.textContent = data.money || '';
      if (compare) {
        compare.textContent = data.compareMoney || '';
        compare.hidden = data.compareOn !== 'true';
      }
      // Afterpay's app owns eligibility and messaging; give it the selected price.
      const priceCents = Number(data.price);
      if (data.price && Number.isFinite(priceCents) && priceCents >= 0) {
        root.querySelectorAll('square-placement[data-platform="shopify-app-blocks"]').forEach(placement => {
          placement.setAttribute('data-amount', (priceCents / 100).toFixed(2));
        });
      }
      const min = Math.max(1, Number(data.quantityMin) || 1);
      const step = Math.max(1, Number(data.quantityStep) || 1);
      const max = Number(data.quantityMax);
      quantity.min = String(min);
      quantity.step = String(step);
      if (Number.isFinite(max) && max >= min) quantity.max = String(max);
      else quantity.removeAttribute('max');
      const chosen = Number(quantity.value);
      if (!Number.isInteger(chosen) || chosen < min || (chosen - min) % step !== 0 || (quantity.max && chosen > max)) {
        quantity.value = String(min);
      }
      add.disabled = !available || planRequired;
      if (accelerated) { accelerated.hidden = !available || planRequired; accelerated.inert = !available || planRequired; }
      add.textContent = planRequired ? 'Purchase option required' : available ? (root.dataset.addLabel || 'Add to cart') : (root.dataset.soldOutLabel || 'Sold out');
      if (availability) availability.textContent = available ? '' : 'This option is currently sold out.';
      if (changeImage) showImage(data);
      if (changeUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', option.value);
        url.searchParams.delete('option_values');
        window.history.replaceState(window.history.state, '', url);
      }
      root.dispatchEvent(new CustomEvent('effy:variant-change', { bubbles: true, detail: { variantId: option.value, available } }));
    }

    select.addEventListener('change', () => update(true, true));
    thumbnails.forEach(button => button.addEventListener('click', () => showImage(button.dataset)));
    const form = root.querySelector('[data-effy-product-form]');
    let submitting = false;
    if (form) form.addEventListener('submit', async event => {
      // Shopify owns accelerated checkout; only Add to cart opens our drawer.
      if (event.defaultPrevented || event.submitter !== add) return;
      event.preventDefault();
      if (submitting || add.disabled || !form.reportValidity()) return;
      const body = new FormData(form);
      body.delete('return_to');
      submitting = true;
      if (accelerated) accelerated.inert = true;
      add.disabled = true;
      select.disabled = true;
      quantity.disabled = true;
      form.setAttribute('aria-busy', 'true');
      add.textContent = 'Adding…';
      if (availability) availability.textContent = '';
      let message = '';
      let added = false;
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 20000);
      try {
        const shopRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const response = await fetch(shopRoot + 'cart/add.js', {
          method: 'POST', body, headers: { Accept: 'application/json' },
          credentials: 'same-origin', signal: controller.signal
        });
        clearTimeout(timer);
        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          message = typeof error.description === 'string' ? error.description : 'We could not add this option. Please check your cart before trying again.';
        } else {
          added = true;
          message = 'Added to your cart.';
          if (!await refreshAndOpenCart(add)) message += ' Use View cart below to see it.';
        }
      } catch (error) {
        message = added ? 'Added to your cart. Use View cart below to see it.' : 'We could not confirm the update. Please check View cart before trying again.';
      } finally {
        clearTimeout(timer);
        submitting = false;
        select.disabled = false;
        quantity.disabled = false;
        form.removeAttribute('aria-busy');
        update(false, false);
        if (availability) availability.textContent = message;
      }
    });
    update(false, false);
  }

  function scan(container = document) {
    if (container.matches && container.matches('[data-effy-product]')) initialize(container);
    container.querySelectorAll('[data-effy-product]').forEach(initialize);
  }
  window.EffyProductMain = { scan };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(), { once: true });
  else scan();
  document.addEventListener('shopify:section:load', event => scan(event.target));
})();
